/* Progressive enhancement. Text, links and native tables remain usable without JS. */
(() => {
  if (window.gcCareHubBooted) return;
  window.gcCareHubBooted = true;
  const instances = new WeakMap();
  const normaliseBody = (body) => {
    // Pasted document typography must not override the shared article design.
    // Keep emphasis, alignment, media sizing and meaningful inline decoration.
    body.querySelectorAll('p, span, div, a, strong, b, em, i, font, h2, h3, h4, h5, h6, ul, ol, li, td, th').forEach(el => {
      if (el.closest('pre, code, svg, [data-gc-preserve-formatting]')) return;
      ['font-family', 'font-size', 'line-height', 'letter-spacing', 'font-variant', 'color', 'background-color', 'white-space'].forEach(prop => el.style.removeProperty(prop));
      if (['normal', '400'].includes(el.style.fontWeight)) el.style.removeProperty('font-weight');
      if (el.style.fontStyle === 'normal') el.style.removeProperty('font-style');
      if (/^(P|H[2-6]|UL|OL|LI)$/.test(el.tagName)) {
        ['margin', 'margin-top', 'margin-bottom', 'margin-left', 'margin-right', 'padding', 'padding-top', 'padding-bottom', 'padding-left', 'padding-right'].forEach(prop => el.style.removeProperty(prop));
      }
      if (el.tagName === 'FONT') ['face', 'size', 'color'].forEach(attr => el.removeAttribute(attr));
      if (!el.getAttribute('style')?.trim()) el.removeAttribute('style');
    });
    // Empty headings create decorative rules; empty paragraphs multiply spacing.
    // Never remove a media container, named anchor or intentional separator.
    body.querySelectorAll('p, h2, h3, h4, h5, h6').forEach(el => {
      if (el.closest('[data-gc-preserve-formatting]') || el.id || el.querySelector('[id], [name], img, picture, video, audio, iframe, svg, canvas, object, embed, input, hr')) return;
      if (!el.textContent.replace(/[\s\u200b\ufeff]/g, '')) el.remove();
    });
    // Recover consecutive typed steps (1. / 2. or 1) / 2)) without guessing at
    // lone numbered paragraphs, dates, decimal values or non-sequential text.
    const marker = p => p?.tagName === 'P' && !p.closest('li, table, blockquote, nav, [data-gc-preserve-formatting]') && !p.querySelector('br, img, iframe') && p.textContent.match(/^\s*([1-9]\d*)[.)]\s+(?=\S)/);
    body.querySelectorAll('p').forEach(first => {
      if (!body.contains(first)) return;
      const start = marker(first);
      if (!start || Number(start[1]) !== 1) return;
      const run = [first];
      for (let next = first.nextElementSibling, n = 2; next; next = next.nextElementSibling, n++) {
        const match = marker(next);
        if (!match || Number(match[1]) !== n) break;
        run.push(next);
      }
      if (run.length < 2) return;
      const ol = document.createElement('ol');
      first.before(ol);
      run.forEach(p => {
        let remaining = marker(p)[0].length;
        const walker = document.createTreeWalker(p, NodeFilter.SHOW_TEXT);
        let text;
        while (remaining && (text = walker.nextNode())) {
          const count = Math.min(remaining, text.textContent.length);
          text.textContent = text.textContent.slice(count);
          remaining -= count;
        }
        const li = document.createElement('li');
        if (p.id) li.id = p.id;
        li.append(...p.childNodes); ol.append(li); p.remove();
      });
    });
  };
  const initialise = (root) => {
    if (instances.has(root)) return;
    const controller = new AbortController();
    instances.set(root, controller);
    const body = root.querySelector('[data-gc-body]');
    if (!body) return;
    const legacy = body.querySelector('#gc-leaning-headstone-guide, #gc-laid-flat-guide');
    if (!legacy) {
      normaliseBody(body);
      body.querySelectorAll('blockquote:not([class])').forEach(el => el.classList.add('gc-expert-note'));
      body.querySelectorAll('ul:not([class])').forEach(el => {
        if (!el.closest('nav, .gc-sources')) el.classList.add('gc-help-list');
      });
      body.querySelectorAll('ol:not([class])').forEach(el => {
        // Bold opening text in every item is the normal-editor convention for process steps.
        if (el.closest('nav') || el.hasAttribute('start') || el.hasAttribute('reversed') || el.hasAttribute('type') || el.querySelector('li[value]')) return;
        const items = Array.from(el.children).filter(x => x.tagName === 'LI');
        const boldOpening = li => {
          const walker = document.createTreeWalker(li, NodeFilter.SHOW_TEXT);
          let text;
          while ((text = walker.nextNode())) {
            if (!text.textContent.trim()) continue;
            return !!text.parentElement.closest('strong, b, [style*="font-weight: bold"], [style*="font-weight: 700"]');
          }
          return false;
        };
        if (items.length && items.every(boldOpening)) el.classList.add('gc-process-list');
      });
      body.querySelectorAll('p').forEach(p => {
        if (p.closest('figure') || p.textContent.trim() || p.querySelectorAll('img').length !== 1) return;
        if (p.children.length !== 1 || !['IMG', 'A'].includes(p.firstElementChild.tagName)) return;
        const figure = document.createElement('figure');
        figure.className = 'gc-body-image';
        const next = p.nextElementSibling;
        const caption = next?.tagName === 'P' && next.children.length === 1 && ['EM','I'].includes(next.firstElementChild.tagName) && next.textContent.trim() === next.firstElementChild.textContent.trim();
        p.replaceWith(figure);
        figure.append(...p.childNodes);
        if (caption) {
          const figcaption = document.createElement('figcaption');
          figcaption.append(...next.firstElementChild.childNodes);
          figure.append(figcaption);
          next.remove();
        }
      });
    }
    body.querySelectorAll('table').forEach((table, ti) => {
      let wrap = table.closest('.gc-table-wrap, .gc-quick-table-wrap');
      if (!wrap) {
        wrap = document.createElement('div'); wrap.className = 'gc-table-wrap';
        table.replaceWith(wrap); wrap.append(table);
      }
      wrap.tabIndex = 0; wrap.setAttribute('role','region');
      wrap.setAttribute('aria-label', table.caption?.textContent.trim() || `Article table ${ti + 1}`);
      const rows = Array.from(table.rows);
      const head = rows[0];
      if (!head || rows.length < 2 || table.querySelector('[colspan]:not([colspan="1"]), [rowspan]:not([rowspan="1"])')) return;
      const headers = Array.from(head.cells);
      const labelled = headers.length > 1 && headers.every(c => c.tagName === 'TH' && c.textContent.trim());
      if (!labelled || rows.slice(1).some(r => r.cells.length !== headers.length || Array.from(r.cells).some(c => c.tagName !== 'TD'))) return;
      if (!head.closest('thead')) {
        const thead = table.createTHead(); thead.append(head);
      }
      headers.forEach(th => th.scope = 'col');
      rows.slice(1).forEach(row => Array.from(row.cells).forEach((cell, i) => cell.dataset.label = headers[i].textContent.trim()));
      table.classList.add('gc-mobile-cards');
    });
    const auto = root.querySelector('[data-gc-auto-contents]');
    const existing = body.querySelector('.gc-contents');
    if (auto && !existing && root.dataset.contents !== 'false') {
      const headings = Array.from(body.querySelectorAll('h2')).filter(h => h.textContent.trim() && !h.closest('.gc-contents, .gc-profile'));
      if (headings.length > 1) {
        headings.forEach((h, i) => {
          if (!h.id) {
            let id = `gc-section-${i + 1}`;
            while (document.getElementById(id)) id += '-section';
            h.id = id;
          }
          const li = document.createElement('li'); const a = document.createElement('a');
          a.href = '#' + encodeURIComponent(h.id); a.textContent = h.textContent.trim();
          li.append(a); auto.querySelector('ol').append(li);
        });
        auto.hidden = false;
      }
    }
    root.querySelectorAll('.gc-contents ol, .gc-process-list, .gc-help-list').forEach(list => list.setAttribute('role', 'list'));
    const bar = root.querySelector('[data-gc-progress]');
    if (bar && root.dataset.progress !== 'false') {
      let queued = false;
      const update = () => {
        queued = false;
        const box = body.getBoundingClientRect();
        const distance = Math.max(1, box.height - window.innerHeight);
        const value = Math.max(0, Math.min(1, -box.top / distance));
        bar.hidden = box.top > window.innerHeight || box.bottom < 0;
        bar.firstElementChild.style.transform = `scaleX(${value})`;
      };
      const schedule = () => { if (!queued) { queued = true; requestAnimationFrame(update); } };
      window.addEventListener('scroll', schedule, {passive:true, signal:controller.signal});
      window.addEventListener('resize', schedule, {signal:controller.signal});
      body.addEventListener('load', schedule, {capture:true, signal:controller.signal});
      update();
    }
  };
  const scan = (scope) => scope.querySelectorAll('[data-gc-article]').forEach(initialise);
  scan(document);
  document.addEventListener('shopify:section:load', e => scan(e.target));
  document.addEventListener('shopify:section:unload', e => e.target.querySelectorAll('[data-gc-article]').forEach(root => { instances.get(root)?.abort(); instances.delete(root); }));
})();
